import { useEffect, useReducer, useRef } from "react";
import { useIntl } from "react-intl";
import { WmButton } from "@watermarkinsights/ripple-react";
import { downloadPDF } from "@/features/common/helper";
import { ExportPillGroup } from "./ExportPillGroup";
import { ExportUserMessage } from "./ExportUserMessage";
import { ExportMessageContent } from "./ExportMessageContent";
import { exportConversationToPdf } from "./exportConversationService";
import {
  downloadChatTranscript,
  extractTextFromMessageContent,
} from "../ai-chat-message-utils";
import type {
  AgentMessage,
  ExportFormat,
  TableSortByMessageId,
  TableSortState,
} from "../ai-chat-message-utils";
import "../draft-message/AiDraftMessage.scss";
import "./AiExportConversation.scss";

interface ExportConversationPanelProps {
  messages: AgentMessage[];
  institutionName: string;
  tableSortByMessageId: TableSortByMessageId;
  onTableSortChange: (messageId: string, sort: TableSortState) => void;
  onBackToConversation: () => void;
  onExportSuccess: () => void;
}

const FORMAT_OPTIONS: { value: ExportFormat; messageId: string }[] = [
  { value: "pdf", messageId: "app.toolboxChat.exportConversation.format.pdf" },
  { value: "txt", messageId: "app.toolboxChat.exportConversation.format.txt" },
];

const EXPORT_BUTTON_MESSAGE_IDS: Record<
  ExportFormat,
  { idle: string; exporting: string }
> = {
  pdf: {
    idle: "app.toolboxChat.exportConversation.action.exportPdf",
    exporting: "app.toolboxChat.exportConversation.action.exportingPdf",
  },
  txt: {
    idle: "app.toolboxChat.exportConversation.action.exportTxt",
    exporting: "app.toolboxChat.exportConversation.action.exportingTxt",
  },
};

type Action =
  | { type: "setFormat"; format: ExportFormat }
  | { type: "toggleId"; id: string }
  | { type: "selectAll"; allIds: string[] }
  | { type: "toggleIncludeMyMessages" }
  | { type: "toggleExpand"; id: string }
  | { type: "exportWithoutSelection" }
  | { type: "exportStart" }
  | { type: "exportSuccess" }
  | { type: "exportError"; error: string };

interface State {
  format: ExportFormat;
  selectedIds: string[];
  includeMyMessages: boolean;
  expandedIds: string[];
  isExporting: boolean;
  error: string | null;
  hasAttemptedWithoutSelection: boolean;
  selectionAttemptCount: number;
}

interface RowRenderOptions {
  roleLabel: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  format: ExportFormat;
  tableSort?: TableSortState;
  onTableSortChange: (sort: TableSortState) => void;
}

function renderExportRowContent(message: AgentMessage, options: RowRenderOptions) {
  switch (message.role) {
    case "user":
      return <ExportUserMessage message={message} roleLabel={options.roleLabel} />;
    case "assistant":
      return (
        <ExportMessageContent
          message={message}
          roleLabel={options.roleLabel}
          isExpanded={options.isExpanded}
          onToggleExpand={options.onToggleExpand}
          format={options.format}
          tableSort={options.tableSort}
          onTableSortChange={options.onTableSortChange}
        />
      );
    default:
      return null;
  }
}

function createInitialState(messages: AgentMessage[]): State {
  return {
    format: "pdf",
    selectedIds: messages.map((message) => message.id),
    includeMyMessages: true,
    expandedIds: [],
    isExporting: false,
    error: null,
    hasAttemptedWithoutSelection: false,
    selectionAttemptCount: 0,
  };
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "setFormat":
      return { ...state, format: action.format };
    case "toggleId": {
      const isSelected = state.selectedIds.includes(action.id);
      return {
        ...state,
        selectedIds: isSelected
          ? state.selectedIds.filter((id) => id !== action.id)
          : [...state.selectedIds, action.id],
        hasAttemptedWithoutSelection: false,
      };
    }
    case "selectAll":
      return {
        ...state,
        selectedIds: action.allIds,
        hasAttemptedWithoutSelection: false,
      };
    case "toggleIncludeMyMessages":
      return {
        ...state,
        includeMyMessages: !state.includeMyMessages,
        hasAttemptedWithoutSelection: false,
      };
    case "toggleExpand": {
      const isExpanded = state.expandedIds.includes(action.id);
      return {
        ...state,
        expandedIds: isExpanded
          ? state.expandedIds.filter((id) => id !== action.id)
          : [...state.expandedIds, action.id],
      };
    }
    case "exportWithoutSelection":
      return {
        ...state,
        hasAttemptedWithoutSelection: true,
        selectionAttemptCount: state.selectionAttemptCount + 1,
      };
    case "exportStart":
      return { ...state, isExporting: true, error: null };
    case "exportSuccess":
      return { ...state, isExporting: false };
    case "exportError":
      return { ...state, isExporting: false, error: action.error };
    default:
      return state;
  }
};

function ExportConversationPanel({
  messages,
  institutionName,
  tableSortByMessageId,
  onTableSortChange,
  onBackToConversation,
  onExportSuccess,
}: ExportConversationPanelProps) {
  const intl = useIntl();
  const [state, dispatch] = useReducer(reducer, messages, createInitialState);
  const {
    format,
    selectedIds,
    includeMyMessages,
    expandedIds,
    isExporting,
    error,
    hasAttemptedWithoutSelection,
    selectionAttemptCount,
  } = state;

  const hasUserMessages = messages.some((message) => message.role === "user");
  const visibleMessages = includeMyMessages
    ? messages
    : messages.filter((message) => message.role !== "user");
  const visibleSelectedIds = selectedIds.filter((id) =>
    visibleMessages.some((message) => message.id === id),
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const selectionErrorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (selectionAttemptCount === 0) return;
    panelRef.current?.scrollTo({ top: 0 });
    selectionErrorRef.current?.focus({ preventScroll: true });
  }, [selectionAttemptCount]);

  const hasNoMessages = messages.length === 0;
  const hasNoSelection = !hasNoMessages && visibleSelectedIds.length === 0;
  const showSelectionError = hasNoSelection && hasAttemptedWithoutSelection;

  const roleUserLabel = intl.formatMessage({
    id: "app.toolboxChat.exportConversation.roleUser",
  });
  const roleAssistantLabel = intl.formatMessage({
    id: "app.toolboxChat.exportConversation.roleAssistant",
  });

  const handleExportAsPdf = async () => {
    const listEl = document.querySelector('[data-export-id="ai-chat-export"]');
    if (!listEl) throw new Error("Export target not found");

    const clone = listEl.cloneNode(true) as HTMLElement;
    clone
      .querySelectorAll(".ecp__row--excluded, .ecp__row-toggle--excluded")
      .forEach((el) => el.remove());
    clone
      .querySelectorAll('input[type="checkbox"], label.chkbx')
      .forEach((checkbox) => checkbox.remove());
    clone
      .querySelectorAll(".ecp__markdown")
      .forEach((el) => el.classList.add("ecp__markdown--expanded"));

    const wrapperDiv = document.createElement("div");
    wrapperDiv.appendChild(clone);

    const header = intl.formatMessage({
      id: "app.toolboxChat.exportConversation.pdfHeaderTitle",
    });
    const subHeader = institutionName;

    const result = await exportConversationToPdf(
      wrapperDiv.outerHTML,
      header,
      subHeader,
    );
    downloadPDF(result.pdf_file_bytes, "Currents_Conversation");
  };

  const handleExportAsTxt = () => {
    const selectedMessages = visibleMessages.filter((message) =>
      visibleSelectedIds.includes(message.id),
    );
    downloadChatTranscript(
      selectedMessages,
      { user: roleUserLabel, assistant: roleAssistantLabel },
      undefined,
      { includeTableData: true, tableSortByMessageId },
    );
  };

  const handleExport = async () => {
    if (hasNoSelection) {
      dispatch({ type: "exportWithoutSelection" });
      return;
    }

    dispatch({ type: "exportStart" });

    try {
      if (format === "pdf") {
        await handleExportAsPdf();
      } else {
        handleExportAsTxt();
      }
      dispatch({ type: "exportSuccess" });
      onExportSuccess();
    } catch {
      dispatch({
        type: "exportError",
        error: intl.formatMessage({ id: "app.toolboxChat.exportConversation.error" }),
      });
    }
  };

  if (hasNoMessages) {
    return (
      <div className="ecp">
        <div className="ecp__empty">
          <svg
            width="19"
            height="23"
            viewBox="0 0 19 23"
            fill="none"
            aria-hidden="true"
            className="ecp__empty-icon"
          >
            <path d="M18.6667 20H0V22.6667H18.6667V20Z" fill="currentColor" />
            <path
              d="M18.6667 8H13.3333V0H5.33333V8H0L9.33333 17.3333L18.6667 8ZM8 10.6667V2.66667H10.6667V10.6667H12.2267L9.33333 13.56L6.44 10.6667H8Z"
              fill="currentColor"
            />
          </svg>
          <p className="ecp__empty-text">
            {intl.formatMessage({ id: "app.toolboxChat.exportConversation.noMessages" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ecp" ref={panelRef}>
        <p className="draft-required-text">
          {intl.formatMessage({
            id: "app.toolboxChat.exportConversation.requiredTextPart1",
          })}
          <span className="draft-required-asterisk"> * </span>
          {intl.formatMessage({
            id: "app.toolboxChat.exportConversation.requiredTextPart2",
          })}
        </p>

        <p className="ecp__intro">
          {intl.formatMessage({ id: "app.toolboxChat.exportConversation.intro" })}
        </p>

        <section className="draft-section">
          <p className="draft-section-label" id="ecp-format-label">
            {intl.formatMessage({ id: "app.toolboxChat.exportConversation.formatLabel" })}
            <span className="draft-required-asterisk">*</span>
          </p>
          <ExportPillGroup
            options={FORMAT_OPTIONS.map(({ value, messageId }) => ({
              value,
              label: intl.formatMessage({ id: messageId }),
            }))}
            selected={format}
            name="ecp-format"
            labelId="ecp-format-label"
            onSelect={(value) => dispatch({ type: "setFormat", format: value })}
          />
        </section>
            <div className="ecp__source-container">
        <div className="ecp__source-header">
          <p
            className={`ecp__source-label${showSelectionError ? " ecp__source-label--error" : ""}`}
            id="ecp-source-label"
          >
            {intl.formatMessage({ id: "app.toolboxChat.exportConversation.sourceLabel" })}
            <span className="ecp__source-count">
              {intl.formatMessage(
                { id: "app.toolboxChat.exportConversation.sourceLabelCount" },
                { count: visibleSelectedIds.length },
              )}
            </span>
            <span className="draft-required-asterisk">*</span>
            {showSelectionError && (
              <span className="ecp__source-warning" aria-hidden="true">
                ⚠
              </span>
            )}
          </p>
          {hasUserMessages && (
            <div className="ecp__include-my-messages">
              <input
                type="checkbox"
                id="ecp-include-my-messages"
                className="chkbx-input"
                checked={includeMyMessages}
                onChange={() => dispatch({ type: "toggleIncludeMyMessages" })}
              />
              <label htmlFor="ecp-include-my-messages" className="chkbx">
                {intl.formatMessage({
                  id: "app.toolboxChat.exportConversation.includeMyMessages",
                })}
              </label>
            </div>
          )}
        </div>

        {showSelectionError && (
          <p
            ref={selectionErrorRef}
            tabIndex={-1}
            className="ecp__no-selection-msg"
            role="alert"
          >
            {intl.formatMessage({ id: "app.toolboxChat.exportConversation.noSelection" })}
          </p>
        )}
        </div>

        {visibleMessages.length > 1 && (
          <div className="ecp__select-all">
            <input
              type="checkbox"
              id="ecp-select-all"
              className="chkbx-input"
              checked={visibleSelectedIds.length === visibleMessages.length}
              onChange={() => {
                const hiddenSelectedIds = selectedIds.filter(
                  (id) => !visibleMessages.some((message) => message.id === id),
                );
                const allIds =
                  visibleSelectedIds.length === visibleMessages.length
                    ? hiddenSelectedIds
                    : [...hiddenSelectedIds, ...visibleMessages.map((message) => message.id)];
                dispatch({ type: "selectAll", allIds });
              }}
            />
            <label htmlFor="ecp-select-all" className="chkbx">
              {intl.formatMessage({ id: "app.toolboxChat.exportConversation.selectAll" })}
            </label>
          </div>
        )}

        <ul
          className="ecp__list"
          data-export-id="ai-chat-export"
          aria-labelledby="ecp-source-label"
        >
          {visibleMessages.map((message) => {
            const isSelected = selectedIds.includes(message.id);
            const previewText = extractTextFromMessageContent(message.content).trim();
            const ariaLabel =
              previewText.slice(0, 60) ||
              intl.formatMessage({
                id: "app.toolboxChat.exportConversation.chartMessageLabel",
              });
            const roleLabel =
              message.role === "user" ? roleUserLabel : roleAssistantLabel;

            return (
              <li
                key={message.id}
                className={`ecp__row${isSelected ? "" : " ecp__row--excluded"}`}
              >
                {visibleMessages.length > 1 && (
                  <>
                    <input
                      type="checkbox"
                      id={`ecp-select-${message.id}`}
                      className="chkbx-input"
                      checked={isSelected}
                      onChange={() => dispatch({ type: "toggleId", id: message.id })}
                      aria-label={ariaLabel}
                    />
                    <label htmlFor={`ecp-select-${message.id}`} className="chkbx" />
                  </>
                )}
                <div className="ecp__row-content">
                  {renderExportRowContent(message, {
                    roleLabel,
                    isExpanded: expandedIds.includes(message.id),
                    onToggleExpand: () =>
                      dispatch({ type: "toggleExpand", id: message.id }),
                    format,
                    tableSort: tableSortByMessageId[message.id],
                    onTableSortChange: (sort) => onTableSortChange(message.id, sort),
                  })}
                </div>
              </li>
            );
          })}
        </ul>

        {error && <p className="ecp__error">{error}</p>}
      </div>

      <div className="ecp__footer">
        <div className="ecp__footer-actions">
          <WmButton buttonType="secondary" onClick={onBackToConversation}>
            {intl.formatMessage({
              id: "app.toolboxChat.exportConversation.action.backToConversation",
            })}
          </WmButton>
          <WmButton
            buttonType="primary"
            icon="#download"
            onClick={handleExport}
            disabled={isExporting}
          >
            {intl.formatMessage({
              id: EXPORT_BUTTON_MESSAGE_IDS[format][isExporting ? "exporting" : "idle"],
            })}
          </WmButton>
        </div>
        {isExporting && (
          <p className="ecp__footer-info" role="status" aria-live="polite">
            {intl.formatMessage({
              id: "app.toolboxChat.exportConversation.pdfDownloadInfo",
            })}
          </p>
        )}
      </div>
    </>
  );
}

export default ExportConversationPanel;
